/**
 * Incremental reader for the StorageProxy bundle stream.
 *
 * A bundle is many DICOM objects concatenated into one chunked HTTP response, so
 * the reader can never assume a network chunk lines up with anything: a frame
 * header can arrive split across three reads, a payload across hundreds. This
 * module is the state machine that turns that byte soup back into whole frames,
 * and it is deliberately pure — no fetch, no cornerstone, no globals — so every
 * boundary case can be driven byte-by-byte from a test.
 *
 * Wire format (all multi-byte integers big-endian), mirroring
 * `BundleEndpoint` in backend/StorageProxy/src/Program.cs:
 *
 *   stream header  'N' 'L' 'B' 'S' | u8 version=1 | u16 count
 *   per entry      u16 index | u8 status | u64 length | payload[length]
 *   trailer        u16 0xFFFF | u8 0xFF | u64 totalPayloadBytes
 *                  | u16 badCount | u16 badIndex[badCount]
 *
 * Entries appear in manifest order, 0..count-1. Non-OK entries carry length 0.
 *
 * Two failure modes are specific to this format and drive the API:
 *
 * 1. A frame can be status OK, carry its full declared length, and still be
 *    GARBAGE. The proxy commits the status line before it has the bytes, so a
 *    member that vanishes from GCS mid-stream is zero-padded to the length it
 *    already promised — that keeps every later frame at the offset the reader
 *    computed, at the cost of one frame of rubble. Those ordinals are named in
 *    the trailer's `badIndexes`, so a caller MUST treat a trailer-named ordinal
 *    as failed no matter how healthy its frame looked.
 *
 * 2. The connection can die before the trailer. Frames already received in full
 *    are still usable (worst case one of them was silently padded and
 *    `parseDicom` rejects it downstream, which the caller already handles as a
 *    per-object fallback). The partial frame at the cut, and everything after
 *    it, simply never happened. `complete: false` tells the caller it never saw
 *    the bad list, so its "everything else succeeded" conclusion is provisional.
 */
/** Frame status byte, matching the proxy's StatusOk/NotFound/TooLarge/GcsError. */
export const BUNDLE_STATUS_OK = 0;
export const BUNDLE_STATUS_NOT_FOUND = 1;
export const BUNDLE_STATUS_SKIPPED_TOO_LARGE = 2;
export const BUNDLE_STATUS_GCS_ERROR = 3;
const STREAM_MAGIC = [0x4e, 0x4c, 0x42, 0x53]; // 'N' 'L' 'B' 'S'
const STREAM_VERSION = 1;
const STREAM_HEADER_LENGTH = 4 + 1 + 2;
const FRAME_HEADER_LENGTH = 2 + 1 + 8;
const TRAILER_FIXED_LENGTH = 2 + 1 + 8 + 2;
const TRAILER_INDEX_SENTINEL = 0xffff;
const TRAILER_STATUS_SENTINEL = 0xff;
/**
 * A cap on one member's declared length, checked before any allocation. The
 * proxy's own per-member ceiling is far below this; the point here is that a
 * corrupt or hostile length field must not talk the browser into reserving
 * gigabytes before the first payload byte arrives.
 */
const MAX_FRAME_BYTES = 512 * 1024 * 1024;
/** Thrown for a stream that is not a bundle at all, or is framed wrong. */
export class BundleStreamError extends Error {
    constructor(message) {
        super(message);
        this.name = 'BundleStreamError';
    }
}
/**
 * Feed-and-drain parser over the bundle byte stream.
 *
 * Usage: `push()` each network chunk and consume the frames it returns, then
 * call `finish()` once the body ends. Pull-style (an async generator over the
 * reader) would read nicer, but this shape keeps the state machine synchronous
 * and therefore trivially testable at one byte per call.
 */
export class BundleStreamParser {
    /**
     * Network chunks received but not yet consumed, held as a queue rather than
     * one growing buffer.
     *
     * The difference is not cosmetic. A member is megabytes and a network chunk
     * is tens of kilobytes, so re-concatenating on every push would copy the
     * partially-assembled payload once per chunk — quadratic in the number of
     * chunks per member. Queuing and materialising once, in `take`, copies every
     * byte of the stream exactly one time.
     */
    queue = [];
    queued = 0;
    phase = 'header';
    count = 0;
    nextIndex = 0;
    frameIndex = 0;
    frameStatus = 0;
    frameLength = 0;
    badIndexes = [];
    trailerBadCount = 0;
    trailerHeaderRead = false;
    trailerComplete = false;
    /**
     * Consume a network chunk, returning every frame it completed (possibly none,
     * possibly several). Throws {@link BundleStreamError} on a malformed stream;
     * the parser must not be reused after that.
     */
    push(chunk) {
        if (chunk.length > 0) {
            this.queue.push(chunk);
            this.queued += chunk.length;
        }
        const frames = [];
        // Each iteration either consumes bytes and continues, or runs out of input
        // and returns — so the loop always terminates on a finite buffer.
        for (;;) {
            if (this.phase === 'done') {
                // Trailing bytes after the trailer mean the sender and the reader
                // disagree about the format; failing loudly beats silently ignoring
                // whatever follows.
                if (this.queued > 0) {
                    throw new BundleStreamError(`${this.queued} trailing bytes after bundle trailer`);
                }
                return frames;
            }
            if (this.phase === 'header') {
                const header = this.take(STREAM_HEADER_LENGTH);
                if (!header)
                    return frames;
                this.readStreamHeader(header);
                continue;
            }
            if (this.phase === 'frame-header') {
                if (this.nextIndex >= this.count) {
                    this.phase = 'trailer';
                    continue;
                }
                const header = this.take(FRAME_HEADER_LENGTH);
                if (!header)
                    return frames;
                this.readFrameHeader(header);
                continue;
            }
            if (this.phase === 'frame-payload') {
                const payload = this.take(this.frameLength);
                if (!payload)
                    return frames;
                frames.push({ index: this.frameIndex, status: this.frameStatus, bytes: payload });
                this.nextIndex = this.frameIndex + 1;
                this.phase = 'frame-header';
                continue;
            }
            // phase === 'trailer'
            if (!this.readTrailer())
                return frames;
        }
    }
    /**
     * Detach exactly `n` bytes from the front of the queue, or null when fewer
     * than that have arrived. The result is always a fresh buffer, so a frame
     * never keeps a whole network chunk alive behind a view.
     */
    take(n) {
        if (this.queued < n)
            return null;
        const out = new Uint8Array(n);
        let filled = 0;
        while (filled < n) {
            const head = this.queue[0];
            const need = n - filled;
            if (head.length <= need) {
                out.set(head, filled);
                filled += head.length;
                this.queue.shift();
            }
            else {
                out.set(head.subarray(0, need), filled);
                this.queue[0] = head.subarray(need);
                filled = n;
            }
        }
        this.queued -= n;
        return out;
    }
    /**
     * Close the stream. Returns the outcome: `complete` is true only when the
     * trailer was read, in which case `badIndexes` is the authoritative list of
     * ordinals whose payload must not be trusted.
     *
     * A truncated stream is NOT an error — the caller keeps whatever frames it
     * already took and refetches the rest per object.
     */
    finish() {
        return {
            badIndexes: this.trailerComplete ? this.badIndexes : [],
            complete: this.trailerComplete,
            count: this.count,
        };
    }
    readStreamHeader(view) {
        for (let i = 0; i < STREAM_MAGIC.length; i++) {
            if (view[i] !== STREAM_MAGIC[i]) {
                throw new BundleStreamError('not a NewLumen bundle stream (bad magic)');
            }
        }
        const version = view[4];
        if (version !== STREAM_VERSION) {
            throw new BundleStreamError(`unsupported bundle stream version ${version}`);
        }
        this.count = readU16(view, 5);
        this.phase = 'frame-header';
    }
    readFrameHeader(view) {
        const index = readU16(view, 0);
        const status = view[2];
        const length = readU64(view, 3);
        // The proxy writes frames strictly in ordinal order, so a mismatch means we
        // have lost sync with the stream and every later offset would be wrong.
        if (index !== this.nextIndex) {
            throw new BundleStreamError(`bundle frame out of order: expected index ${this.nextIndex}, got ${index}`);
        }
        if (length > MAX_FRAME_BYTES) {
            throw new BundleStreamError(`bundle frame ${index} declares ${length} bytes`);
        }
        if (status !== BUNDLE_STATUS_OK && length !== 0) {
            throw new BundleStreamError(`bundle frame ${index} has status ${status} but declares ${length} bytes`);
        }
        this.frameIndex = index;
        this.frameStatus = status;
        this.frameLength = length;
        this.phase = 'frame-payload';
    }
    /**
     * Returns false when more bytes are needed. The trailer is read in two steps
     * — fixed part, then the variable badIndex array — because the two can be
     * split across reads; the fixed-length guard therefore has to sit INSIDE the
     * first step, or a trailer whose bad list arrives late is never finished.
     */
    readTrailer() {
        if (!this.trailerHeaderRead) {
            const view = this.take(TRAILER_FIXED_LENGTH);
            if (!view)
                return false;
            const sentinelIndex = readU16(view, 0);
            const sentinelStatus = view[2];
            if (sentinelIndex !== TRAILER_INDEX_SENTINEL || sentinelStatus !== TRAILER_STATUS_SENTINEL) {
                throw new BundleStreamError('bundle trailer sentinel missing');
            }
            // totalDeclaredPayloadBytes at offset 3 is informational: every frame's
            // length was already read individually, so re-deriving it here buys
            // nothing the frame loop has not already enforced.
            this.trailerBadCount = readU16(view, 2 + 1 + 8);
            this.trailerHeaderRead = true;
        }
        const badList = this.take(this.trailerBadCount * 2);
        if (!badList)
            return false;
        for (let i = 0; i < this.trailerBadCount; i++) {
            this.badIndexes.push(readU16(badList, i * 2));
        }
        // Only now is the bad list whole; a stream cut between the two steps must
        // still report `complete: false`, since the list it carries is a prefix.
        this.trailerComplete = true;
        this.phase = 'done';
        return true;
    }
}
function readU16(bytes, offset) {
    return (bytes[offset] << 8) | bytes[offset + 1];
}
/**
 * Big-endian u64. Read as two u32 halves and recombined in floating point:
 * lengths here are object sizes capped well under 2^53, so Number is exact and
 * avoids threading BigInt through every arithmetic site.
 */
function readU64(bytes, offset) {
    const high = bytes[offset] * 0x1000000 +
        (bytes[offset + 1] << 16) +
        (bytes[offset + 2] << 8) +
        bytes[offset + 3];
    const low = bytes[offset + 4] * 0x1000000 +
        (bytes[offset + 5] << 16) +
        (bytes[offset + 6] << 8) +
        bytes[offset + 7];
    return high * 0x100000000 + low;
}
